import { memo, useCallback, useMemo, useState, type ReactNode } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import type { CheckedState } from '@radix-ui/react-checkbox';
import { CheckIcon } from '@radix-ui/react-icons';
import { ChevronDown, ChevronRight, Minus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { getContentTypeIcon } from '@/lib/content-labels';
import type { DeletionEdgeKind, DeletionImpact, DeletionImpactNode } from '@vivport/backend/types';
import { BLOCK_REASON_COPY, nodeTitle, nodeTypeLabel } from './deletion-labels';

export interface DeletionImpactTreeProps {
  impact: DeletionImpact;
  optionalIncluded: Set<number>;
  onToggle: (contentId: number, next: boolean) => void;
  disabled: boolean;
}

const DEFAULT_EXPANDED_LEVELS = 2;
const MAX_INDENT_LEVEL = 6;
const INDENT_REM = 1.25;

// A FORCED node is a document section, a thread reply, or a collection member. The edge
// that puts it there cannot outlive its parent, so it is structurally inseparable from
// what the member selected and there is nothing to toggle. The chip names the structure
// holding it rather than leaving a disabled tick unexplained. An ATTACHMENT edge is
// always OPTIONAL, so it never reaches this map.
const FORCED_EDGE_CHIP: Record<DeletionEdgeKind, string | null> = {
  DOCUMENT_HIERARCHY: 'part of this document',
  MESSAGE_THREAD: 'part of this thread',
  COLLECTION_MEMBER: 'in this collection',
  ATTACHMENT: null,
};

interface TreeShape {
  nodeById: Map<number, DeletionImpactNode>;
  childrenByParent: Map<number, number[]>;
  rootIds: number[];
  orphanedParentIds: Set<number>;
  postOrder: number[];
}

function buildTreeShape(nodes: DeletionImpactNode[]): TreeShape {
  const nodeById = new Map<number, DeletionImpactNode>();
  for (const node of nodes) nodeById.set(node.contentId, node);

  const childrenByParent = new Map<number, number[]>();
  const rootIds: number[] = [];
  const orphanedParentIds = new Set<number>();

  for (const node of nodes) {
    const parentId = node.treeParentId;
    if (parentId === null) {
      rootIds.push(node.contentId);
      continue;
    }
    if (!nodeById.has(parentId)) {
      // The impact cap can drop a parent while keeping its child. Surfacing the child at
      // top level with a note beats dropping a row the member is about to delete.
      rootIds.push(node.contentId);
      orphanedParentIds.add(node.contentId);
      continue;
    }
    const siblings = childrenByParent.get(parentId);
    if (siblings) siblings.push(node.contentId);
    else childrenByParent.set(parentId, [node.contentId]);
  }

  // Deepest-first ordering, produced once here so the per-selection indeterminate pass
  // can read each child answer before the node that needs it.
  const postOrder: number[] = [];
  const stack: number[] = [...rootIds].reverse();
  const descended = new Set<number>();
  while (stack.length > 0) {
    const id = stack[stack.length - 1];
    const children = childrenByParent.get(id);
    if (children && !descended.has(id)) {
      descended.add(id);
      for (let i = children.length - 1; i >= 0; i -= 1) stack.push(children[i]);
      continue;
    }
    stack.pop();
    postOrder.push(id);
  }

  return { nodeById, childrenByParent, rootIds, orphanedParentIds, postOrder };
}

// A node is indeterminate when it is not itself included but something beneath it is.
// Walking deepest-first lets each node read an answer its children already computed, so
// the whole tree costs one pass rather than a descendant walk per row.
function buildHasIncludedDescendant(
  shape: TreeShape,
  optionalIncluded: Set<number>
): Map<number, boolean> {
  const hasIncludedDescendant = new Map<number, boolean>();
  for (const id of shape.postOrder) {
    const children = shape.childrenByParent.get(id) ?? [];
    hasIncludedDescendant.set(
      id,
      children.some(
        (childId) => optionalIncluded.has(childId) || hasIncludedDescendant.get(childId) === true
      )
    );
  }
  return hasIncludedDescendant;
}

// A child cannot be deleted while its parent survives to hold the edge, so including a
// node must include every OPTIONAL ancestor up to a root, and excluding one must exclude
// every OPTIONAL descendant. Blocked nodes are never going, so neither closure touches
// them. The parent component holds only a flat Set and cannot infer either direction,
// which is why the tree resolves both here.
function optionalAncestorIds(id: number, shape: TreeShape): number[] {
  const out: number[] = [];
  let current = shape.nodeById.get(id)?.treeParentId ?? null;
  while (current !== null) {
    const parent = shape.nodeById.get(current);
    if (!parent) break;
    if (parent.inclusion === 'OPTIONAL' && parent.blocked.length === 0) out.push(parent.contentId);
    current = parent.treeParentId;
  }
  return out;
}

function optionalDescendantIds(id: number, shape: TreeShape): number[] {
  const out: number[] = [];
  const queue = [...(shape.childrenByParent.get(id) ?? [])];
  while (queue.length > 0) {
    const childId = queue.shift() as number;
    const child = shape.nodeById.get(childId);
    if (!child) continue;
    if (child.inclusion === 'OPTIONAL' && child.blocked.length === 0) out.push(childId);
    queue.push(...(shape.childrenByParent.get(childId) ?? []));
  }
  return out;
}

function rowCheckState(
  node: DeletionImpactNode,
  optionalIncluded: Set<number>,
  hasIncludedDescendant: Map<number, boolean>
): CheckedState {
  if (node.blocked.length > 0) return false;
  if (node.inclusion === 'ROOT' || node.inclusion === 'FORCED') return true;
  if (optionalIncluded.has(node.contentId)) return true;
  return hasIncludedDescendant.get(node.contentId) === true ? 'indeterminate' : false;
}

// The shared components/ui/checkbox.tsx hard-codes a tick inside its Indicator and
// spreads props onto the Root, so its children cannot be overridden: it structurally
// cannot render the mixed state this tree needs. Rendering the Radix primitive here
// keeps the theme tokens and the aria-checked="mixed" Radix emits, without changing a
// primitive used across the whole app.
function TreeCheckbox({
  state,
  disabled,
  label,
  onChange,
}: {
  state: CheckedState;
  disabled: boolean;
  label: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <CheckboxPrimitive.Root
      checked={state}
      disabled={disabled}
      onCheckedChange={(next) => onChange(next === true)}
      aria-label={label}
      className={cn(
        'mt-0.5 h-4 w-4 shrink-0 rounded-sm border border-primary shadow focus-visible:outline-none',
        'focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
        'data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground'
      )}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
        {state === 'indeterminate' ? (
          <Minus className="h-3 w-3" />
        ) : (
          <CheckIcon className="h-4 w-4" />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

interface DeletionImpactTreeRowProps {
  node: DeletionImpactNode;
  level: number;
  childCount: number;
  expanded: boolean;
  orphanedParent: boolean;
  checkState: CheckedState;
  interactive: boolean;
  disabled: boolean;
  onToggleExpand: (contentId: number, nextExpanded: boolean) => void;
  onToggleNode: (contentId: number, next: boolean) => void;
}

const DeletionImpactTreeRow = memo(function DeletionImpactTreeRow({
  node,
  level,
  childCount,
  expanded,
  orphanedParent,
  checkState,
  interactive,
  disabled,
  onToggleExpand,
  onToggleNode,
}: DeletionImpactTreeRowProps) {
  const Icon = getContentTypeIcon(node.contentType);
  const blocked = node.blocked.length > 0;
  const chip = node.inclusion === 'FORCED' && node.edgeKind ? FORCED_EDGE_CHIP[node.edgeKind] : null;
  const alsoCount = node.alsoReachableFrom.length;
  const title = nodeTitle(node);

  const checkbox = (
    <TreeCheckbox
      state={checkState}
      disabled={disabled || !interactive}
      label={title}
      onChange={(next) => onToggleNode(node.contentId, next)}
    />
  );

  return (
    <div
      className="flex items-start gap-2 py-1 pr-2 text-sm"
      // Indent from the rendered position, not node.depth: a node whose parent was cut
      // by the impact cap renders at top level while still carrying a deep depth.
      style={{ paddingLeft: `${Math.min(level, MAX_INDENT_LEVEL) * INDENT_REM}rem` }}
    >
      {childCount > 0 ? (
        <button
          type="button"
          onClick={() => onToggleExpand(node.contentId, !expanded)}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
          className="mt-0.5 shrink-0 rounded-sm text-muted-foreground hover:text-foreground"
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      ) : (
        <span className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      )}

      {node.inclusion === 'ROOT' && !blocked ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex shrink-0">{checkbox}</span>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent>
              You selected this. Deselect it in the list behind this dialog.
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      ) : (
        checkbox
      )}

      <Icon
        className={cn('mt-0.5 h-4 w-4 shrink-0 text-muted-foreground', blocked && 'opacity-50')}
        aria-hidden="true"
      />

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={cn('font-medium', blocked && 'text-muted-foreground line-through')}>
            {title}
          </span>
          <span className="text-xs text-muted-foreground">{nodeTypeLabel(node)}</span>
          {chip && (
            <Badge variant="secondary" className="font-normal">
              {chip}
            </Badge>
          )}
          {node.revisionRowCount > 1 && (
            <Badge variant="outline" className="font-normal">
              {node.revisionRowCount} revisions
            </Badge>
          )}
          {node.orphanCount > 0 && (
            <Badge variant="destructive" className="font-normal">
              {node.orphanCount} will be orphaned
            </Badge>
          )}
          {alsoCount > 0 && (
            <Badge variant="outline" className="font-normal">
              also filed under {alsoCount} other {alsoCount === 1 ? 'item' : 'items'} in this list
            </Badge>
          )}
          {childCount > 0 && !expanded && (
            <span className="text-xs text-muted-foreground">{childCount} inside</span>
          )}
        </div>
        {blocked && (
          <p className="text-xs text-muted-foreground">{BLOCK_REASON_COPY[node.blocked[0]]}</p>
        )}
        {orphanedParent && <p className="text-xs text-muted-foreground">Parent not shown.</p>}
      </div>
    </div>
  );
});

export function DeletionImpactTree({
  impact,
  optionalIncluded,
  onToggle,
  disabled,
}: DeletionImpactTreeProps) {
  // Expansion is an override map rather than a seeded set: a refetch brings new nodes,
  // and those should still follow the depth default instead of arriving collapsed.
  const [expansionOverrides, setExpansionOverrides] = useState<Map<number, boolean>>(new Map());

  const shape = useMemo(() => buildTreeShape(impact.nodes), [impact.nodes]);
  const hasIncludedDescendant = useMemo(
    () => buildHasIncludedDescendant(shape, optionalIncluded),
    [shape, optionalIncluded]
  );

  const handleToggleExpand = useCallback((contentId: number, nextExpanded: boolean) => {
    setExpansionOverrides((prev) => {
      const updated = new Map(prev);
      updated.set(contentId, nextExpanded);
      return updated;
    });
  }, []);

  const handleToggleNode = useCallback(
    (contentId: number, next: boolean) => {
      // Both id lists are resolved before any onToggle fires: the parent Set does not
      // update between calls, so the closure cannot be read back out of it mid-flight.
      const affected = next
        ? [contentId, ...optionalAncestorIds(contentId, shape)]
        : [contentId, ...optionalDescendantIds(contentId, shape)];
      for (const id of affected) onToggle(id, next);
    },
    [shape, onToggle]
  );

  // Every checkbox is dead while the list is partial: picking items individually would
  // imply the unlisted ones can be picked too.
  const rowsDisabled = disabled || impact.truncated;

  const rows: ReactNode[] = [];
  const pushRows = (ids: number[], level: number) => {
    for (const id of ids) {
      const node = shape.nodeById.get(id);
      if (!node) continue;
      const children = shape.childrenByParent.get(id) ?? [];
      const expanded = expansionOverrides.get(id) ?? level < DEFAULT_EXPANDED_LEVELS;
      rows.push(
        <DeletionImpactTreeRow
          key={id}
          node={node}
          level={level}
          childCount={children.length}
          expanded={expanded}
          orphanedParent={shape.orphanedParentIds.has(id)}
          checkState={rowCheckState(node, optionalIncluded, hasIncludedDescendant)}
          interactive={node.inclusion === 'OPTIONAL' && node.blocked.length === 0}
          disabled={rowsDisabled}
          onToggleExpand={handleToggleExpand}
          onToggleNode={handleToggleNode}
        />
      );
      if (children.length > 0 && expanded) pushRows(children, level + 1);
    }
  };
  pushRows(shape.rootIds, 0);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="rounded-md border">
        <div className="max-h-72 overflow-y-auto p-2">{rows}</div>
        {impact.truncated && (
          <p className="border-t px-3 py-2 text-xs text-muted-foreground">
            {impact.truncatedCount} further{' '}
            {impact.truncatedCount === 1 ? 'item is' : 'items are'} affected and not listed. The
            switch above is the only control that applies to them.
          </p>
        )}
      </div>
    </TooltipProvider>
  );
}
